import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { useCompanyPackageAccess } from "@/hooks/useCompanyPackageAccess";
import { useCompanyUsage } from "@/hooks/useCompanyUsage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Upload, Search, Check, Loader2, FolderOpen, Hash, Tag, Sparkles } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "@/components/media/types";

interface MediaAsset {
  id: string;
  file_name: string;
  file_url: string;
  alt_text: string | null;
  mime_type: string;
  width: number | null;
  height: number | null;
  file_size: number;
  folder: string | null;
  tags: string[] | null;
}

interface ArticleContext {
  title?: string;
  tags?: string[];
  categoryName?: string;
}

interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string, altText?: string | null) => void;
  articleContext?: ArticleContext;
}

export default function MediaPickerDialog({ open, onOpenChange, onSelect, articleContext }: MediaPickerDialogProps) {
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const { canCreateMedia, mediaLimit } = useCompanyPackageAccess();
  const { usage } = useCompanyUsage();
  const { toast } = useToast();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MediaAsset | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchAssets = useCallback(async () => {
    if (!user || !currentCompanyId) return;
    setLoading(true);
    let allData: any[] = [];
    let from = 0;
    const FETCH_SIZE = 1000;
    while (true) {
      const { data, error } = await supabase
        .from("media_assets")
        .select("id, file_name, file_url, alt_text, mime_type, width, height, file_size, folder, tags")
        .eq("company_id", currentCompanyId)
        .ilike("mime_type", "image/%")
        .order("created_at", { ascending: false })
        .range(from, from + FETCH_SIZE - 1);
      if (error) break;
      allData = allData.concat(data || []);
      if (!data || data.length < FETCH_SIZE) break;
      from += FETCH_SIZE;
    }
    setAssets(allData);
    setLoading(false);
  }, [user, currentCompanyId]);

  useEffect(() => {
    if (open) {
      fetchAssets();
      setSelected(null);
      setActiveCategory(null);
      setActiveTags([]);
      setSearch("");
    }
  }, [open, fetchAssets]);

  // Computed: category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    assets.forEach((a) => {
      const cat = a.folder || "uncategorized";
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [assets]);

  // Computed: all tags
  const allTags = useMemo(() => {
    const tagMap: Record<string, number> = {};
    assets.forEach((a) => {
      (a.tags || []).forEach((t) => { tagMap[t] = (tagMap[t] || 0) + 1; });
    });
    return Object.entries(tagMap)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  }, [assets]);

  // Suggested images based on article context
  const suggestedImages = useMemo(() => {
    if (!articleContext || assets.length === 0) return [];

    const keywords: string[] = [];
    // Extract keywords from title
    if (articleContext.title) {
      const titleWords = articleContext.title.toLowerCase().split(/\s+/).filter((w) => w.length > 3);
      keywords.push(...titleWords);
    }
    // Add article tags
    if (articleContext.tags) {
      keywords.push(...articleContext.tags.map((t) => t.toLowerCase()));
    }
    // Add category name
    if (articleContext.categoryName) {
      keywords.push(...articleContext.categoryName.toLowerCase().split(/[\s&,]+/).filter((w) => w.length > 2));
    }

    if (keywords.length === 0) return [];

    // Keyword-to-folder mapping for better matching
    const keywordFolderMap: Record<string, string[]> = {
      ai: ["tech-ai"], intelligence: ["tech-ai"], tech: ["tech-ai"], software: ["tech-ai"], machine: ["tech-ai"],
      cyber: ["cyber-security"], hack: ["cyber-security"], security: ["cyber-security"], malware: ["cyber-security"],
      code: ["programming"], programming: ["programming"], developer: ["programming"], javascript: ["programming"],
      economy: ["economy-trade"], trade: ["economy-trade"], stock: ["economy-trade"], finance: ["economy-trade"], market: ["economy-trade"],
      corporate: ["corporate-brands"], brand: ["corporate-brands", "brand-logos"], office: ["corporate-brands"],
      entrepreneur: ["entrepreneurship"], startup: ["entrepreneurship"], business: ["entrepreneurship"],
      politic: ["politics-world"], government: ["politics-world"], election: ["politics-world"],
      science: ["science"], research: ["science"], laboratory: ["science"],
      sport: ["sports"], football: ["sports"], olympic: ["sports"],
      lifestyle: ["lifestyle-society"], society: ["lifestyle-society"], culture: ["lifestyle-society"],
      space: ["space-aero"], aerospace: ["space-aero"], nasa: ["space-aero"], rocket: ["space-aero"],
      breaking: ["uncategorized"], news: ["uncategorized"],
    };

    // Score each asset
    const scored = assets.map((asset) => {
      let score = 0;
      const assetTags = (asset.tags || []).map((t) => t.toLowerCase());
      const assetFolder = (asset.folder || "").toLowerCase();
      const assetName = asset.file_name.toLowerCase();
      const assetAlt = (asset.alt_text || "").toLowerCase();

      for (const kw of keywords) {
        // Direct tag match (high value)
        if (assetTags.some((t) => t.includes(kw) || kw.includes(t))) score += 3;
        // Folder match via keyword mapping
        const mappedFolders = Object.entries(keywordFolderMap)
          .filter(([key]) => kw.includes(key))
          .flatMap(([, folders]) => folders);
        if (mappedFolders.includes(assetFolder)) score += 4;
        // Direct folder match
        if (assetFolder.includes(kw)) score += 2;
        // File name / alt text match
        if (assetName.includes(kw)) score += 1;
        if (assetAlt.includes(kw)) score += 1;
      }
      return { asset, score };
    });

    const results = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score).slice(0, 5).map((s) => s.asset);

    // Fallback: if no matches, show corporate/breaking news images
    if (results.length === 0) {
      const fallback = assets
        .filter((a) => ["corporate-brands", "uncategorized", "tech-ai"].includes(a.folder || ""))
        .slice(0, 5);
      return fallback;
    }

    return results;
  }, [assets, articleContext]);

  // Filtering
  const filtered = useMemo(() => {
    let result = [...assets];

    if (activeCategory) {
      result = result.filter((a) => (a.folder || "uncategorized") === activeCategory);
    }

    if (activeTags.length > 0) {
      result = result.filter((a) => activeTags.every((t) => (a.tags || []).includes(t)));
    }

    if (search) {
      const q = search.toLowerCase().trim();
      if (q.startsWith("tag:")) {
        const tagQ = q.slice(4).trim();
        if (tagQ) result = result.filter((a) => (a.tags || []).some((t) => t.includes(tagQ)));
      } else if (q.startsWith("cat:")) {
        const catQ = q.slice(4).trim();
        if (catQ) result = result.filter((a) => (a.folder || "uncategorized").includes(catQ));
      } else {
        result = result.filter((a) =>
          a.file_name.toLowerCase().includes(q) ||
          (a.alt_text || "").toLowerCase().includes(q) ||
          (a.tags || []).some((t) => t.includes(q))
        );
      }
    }

    return result;
  }, [assets, activeCategory, activeTags, search]);

  const handleUpload = async (files: FileList) => {
    if (!user || !currentCompanyId || !files.length) return;
    
    // Check media limit
    if (!canCreateMedia) {
      toast({ title: "Media upload not available", description: "Media library is not included in your plan", variant: "destructive" });
      return;
    }
    
    if (mediaLimit && usage.media_assets_used >= mediaLimit) {
      toast({ title: "Limit reached", description: `You've reached your media asset limit of ${mediaLimit}`, variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Check limit before each upload
        if (mediaLimit && usage.media_assets_used >= mediaLimit) {
          toast({ title: "Upload stopped", description: `You've reached your media asset limit of ${mediaLimit}`, variant: "destructive" });
          break;
        }

        const ext = file.name.split(".").pop();
        const path = `${currentCompanyId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("media").upload(path, file);
        if (uploadErr) throw uploadErr;

        const { data: urlData } = supabase.storage.from("media").getPublicUrl(path);

        let width: number | null = null;
        let height: number | null = null;
        if (file.type.startsWith("image/")) {
          const dims = await getImageDimensions(file);
          width = dims.width;
          height = dims.height;
        }

        const { error: dbErr } = await supabase.from("media_assets").insert({
          user_id: user.id,
          company_id: currentCompanyId,
          file_name: file.name,
          file_path: path,
          file_url: urlData.publicUrl,
          file_size: file.size,
          mime_type: file.type,
          width,
          height,
          folder: "uncategorized",
          tags: [],
        });
        if (dbErr) throw dbErr;
      }
      toast({ title: "Upload réussi" });
      fetchAssets();
    } catch (err: any) {
      toast({ title: "Erreur d'upload", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  };

  const totalCount = assets.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle>Choisir une image</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-48 flex-shrink-0 border-r border-border flex flex-col">
            <div className="p-3 border-b border-border">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-2">
                <FolderOpen className="h-3 w-3" /> Catégories
              </h3>
              <div className="space-y-0.5">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
                    activeCategory === null ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-accent"
                  }`}
                >
                  <span>Tous</span>
                  <span className="text-[10px] text-muted-foreground">{totalCount}</span>
                </button>
                {CATEGORIES.map((cat) => {
                  const count = categoryCounts[cat] || 0;
                  if (count === 0) return null;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
                        activeCategory === cat ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-accent"
                      }`}
                    >
                      <span>{CATEGORY_LABELS[cat] || cat}</span>
                      <span className="text-[10px] text-muted-foreground">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {allTags.length > 0 && (
              <div className="p-3 flex-1 min-h-0">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-2">
                  <Tag className="h-3 w-3" /> Tags
                </h3>
                <ScrollArea className="h-[calc(100%-24px)]">
                  <div className="flex flex-wrap gap-1">
                    {allTags.slice(0, 20).map(({ tag, count }) => (
                      <button key={tag} onClick={() => toggleTag(tag)}>
                        <Badge
                          variant={activeTags.includes(tag) ? "default" : "outline"}
                          className="text-[10px] cursor-pointer hover:bg-primary/20 transition-colors gap-0.5"
                        >
                          <Hash className="h-2.5 w-2.5" />
                          {tag}
                          <span className="opacity-60">{count}</span>
                        </Badge>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex gap-2 px-4 py-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Rechercher… (tag:modern, cat:icons)" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 text-xs" />
              </div>
              <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Upload
              </Button>
              <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && handleUpload(e.target.files)} />
            </div>

            <p className="px-4 text-[10px] text-muted-foreground mb-1">
              {filtered.length} image{filtered.length !== 1 ? "s" : ""}
              {activeCategory && ` · ${CATEGORY_LABELS[activeCategory] || activeCategory}`}
              {activeTags.length > 0 && ` · ${activeTags.join(", ")}`}
            </p>

            <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filtered.length === 0 && suggestedImages.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  Aucune image trouvée.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Suggested images row */}
                  {suggestedImages.length > 0 && !activeCategory && !search && activeTags.length === 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs font-semibold text-foreground">Suggestions pour cet article</span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {suggestedImages.map((asset) => (
                          <button
                            key={`sug-${asset.id}`}
                            onClick={() => setSelected(asset)}
                            className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                              selected?.id === asset.id ? "border-primary ring-2 ring-primary/20" : "border-amber-500/30"
                            }`}
                          >
                            <img src={asset.file_url} alt={asset.alt_text || asset.file_name} className="h-full w-full object-cover" loading="lazy" />
                            {selected?.id === asset.id && (
                              <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                <Check className="h-5 w-5 text-primary-foreground bg-primary rounded-full p-0.5" />
                              </div>
                            )}
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                              <p className="text-[8px] text-white truncate">{asset.file_name}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All images grid */}
                  {filtered.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {filtered.slice(0, 200).map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => setSelected(asset)}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                            selected?.id === asset.id ? "border-primary ring-2 ring-primary/20" : "border-border"
                          }`}
                        >
                          <img src={asset.file_url} alt={asset.alt_text || asset.file_name} className="h-full w-full object-cover" loading="lazy" />
                          {selected?.id === asset.id && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <Check className="h-6 w-6 text-primary-foreground bg-primary rounded-full p-1" />
                            </div>
                          )}
                          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                            <p className="text-[9px] text-white truncate">{asset.file_name}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 pb-5 pt-2 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button disabled={!selected} onClick={() => { if (selected) { onSelect(selected.file_url, selected.alt_text); onOpenChange(false); } }}>
            Sélectionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(img.src); };
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}
